import { useEffect, useRef } from "react";
import DailyIframe from "@daily-co/daily-js";
import { X } from "lucide-react";

export default function AriaSessionModal({ roomUrl, token, onClose }) {
  const frameHostRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);

    // Join via Daily's official SDK (renders the exact same in-call UI as the
    // previous plain iframe). This enables the Android audio fix below.
    // Creation is deferred one tick: React StrictMode double-invokes effects in
    // dev, and daily-js forbids duplicate instances — the first (immediately
    // cleaned-up) invocation cancels its own timer before any frame exists.
    let call = null;
    const timer = setTimeout(() => {
      call = DailyIframe.createFrame(frameHostRef.current);

      // Android Chrome routes WebRTC voice-call audio to the earpiece by default,
      // which sounds extremely quiet. Delegating "speaker-selection" lets the
      // embedded call switch output devices (its own speaker toggle + our
      // programmatic selection below). Without it, setSinkId is blocked inside a
      // cross-origin frame and the user is stuck on the earpiece.
      const frameEl = call.iframe();
      if (frameEl) {
        frameEl.setAttribute(
          "allow",
          "microphone; camera; autoplay; display-capture; screen-wake-lock; compute-pressure; speaker-selection"
        );
        frameEl.setAttribute("data-testid", "aria-frame");
        frameEl.setAttribute("title", "Aria — live AI companion session");
      }

      // Once in the call, prefer the loudspeaker when the device exposes one.
      const preferSpeaker = async () => {
        try {
          const res = await call.enumerateDevices();
          const devices = Array.isArray(res) ? res : res?.devices || [];
          const speaker = devices.find(
            (d) => d.kind === "audiooutput" && /speaker/i.test(d.label || "")
          );
          if (speaker) {
            await call.setOutputDeviceAsync({ outputDeviceId: speaker.deviceId });
          }
        } catch {
          // Non-fatal: in-call audio settings still work via speaker-selection above.
        }
      };
      call.on("joined-meeting", preferSpeaker);
      call.on("left-meeting", () => onCloseRef.current());
      // ANDROID AUDIO FIX: Chrome for Android cannot switch audio output from
      // the page (setSinkId is unsupported there) and routes AUDIO-ONLY WebRTC
      // participation to the quiet earpiece (voice-call channel), while VIDEO
      // calls are routed to the loudspeaker. Joining with the camera on makes
      // Android treat the session as a video call, so Aria plays loudly through
      // the speaker. Users can still toggle the camera off in-call if they wish.
      call.join({ url: roomUrl, token, startVideoOff: false });
    }, 0);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(timer);
      if (call) {
        try {
          call.destroy();
        } catch {}
      }
    };
  }, [roomUrl, token]);

  return (
    <div
      data-testid="aria-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Aria live session"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
    >
      <div className="flex h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-cyan-300/25 bg-[#0a0a0a] shadow-[0_30px_120px_rgba(0,229,255,0.12)]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
          <div>
            <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-cyan-300/90">
              Live Early Preview · AI Companion
            </p>
            <p className="font-display mt-1 text-sm font-medium tracking-tight text-white">Aria</p>
          </div>
          <button
            data-testid="aria-close"
            onClick={() => onCloseRef.current()}
            aria-label="End session"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-cyan-300/50 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <div
          ref={frameHostRef}
          data-testid="aria-frame-host"
          className="min-h-0 flex-1 bg-black [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:border-0"
        />
        <p className="border-t border-white/10 px-5 py-2.5 text-center text-[10px] uppercase tracking-[0.2em] text-white/35">
          Powered by Lemon Slice
        </p>
      </div>
    </div>
  );
}
